// src/utils/alarmConfig.js

export const ALARM_EVENTS = {
  // 🚨 Critical Alarms
  'sos': { priority: 'critical', vibrate: [200, 100, 200, 100, 200] },
  'sos_alarm': { priority: 'critical', vibrate: [200, 100, 200, 100, 200] },
  'panic': { priority: 'critical', vibrate: [200, 100, 200, 100, 200] },
  'panic_button': { priority: 'critical', vibrate: [200, 100, 200, 100, 200] },
  'tamper': { priority: 'critical', vibrate: [200, 100, 200] },
  'tampering': { priority: 'critical', vibrate: [200, 100, 200] },
  'tow': { priority: 'critical', vibrate: [200, 100, 200] },
  'towing_alarm': { priority: 'critical', vibrate: [200, 100, 200] },
  'collision': { priority: 'critical', vibrate: [300, 100, 300] },
  'collision_alarm': { priority: 'critical', vibrate: [300, 100, 300] },
  'rollover_alarm': { priority: 'critical', vibrate: [300, 100, 300] },
  
  // ⚡ High Priority - Movement
  'overspeed': { priority: 'high', vibrate: [150, 50, 150] },
  'overspeed_alarm': { priority: 'high', vibrate: [150, 50, 150] },
  'speeding': { priority: 'high', vibrate: [150, 50, 150] },
  'harsh_braking': { priority: 'high', vibrate: [100, 50, 100] },
  'harsh_acceleration': { priority: 'high', vibrate: [100, 50, 100] },
  'harsh_cornering': { priority: 'high', vibrate: [100, 50, 100] },
  
  // 🔑 Medium - Engine/Ignition
  'ignition_on': { priority: 'medium', vibrate: [50] },
  'ignition_off': { priority: 'medium', vibrate: [50] },
  'acc_on': { priority: 'medium', vibrate: [40] },
  'acc_off': { priority: 'medium', vibrate: [40] },
  'engine_start': { priority: 'medium', vibrate: [50] },
  'engine_stop': { priority: 'medium', vibrate: [50] },
  
  // 📍 Medium - Geofence
  'geofence_entry': { priority: 'medium', vibrate: [80] },
  'geofence_exit': { priority: 'medium', vibrate: [100, 50, 100] },
  'geofence_violation': { priority: 'high', vibrate: [200, 100, 200] },
  'enter_area': { priority: 'medium', vibrate: [80] },
  'exit_area': { priority: 'medium', vibrate: [100, 50, 100] },
  
  // 🔋 Power/Battery
  'low_battery': { priority: 'medium', vibrate: [100, 50, 100] },
  'battery_low': { priority: 'medium', vibrate: [100, 50, 100] },
  'power_cut': { priority: 'high', vibrate: [150, 50, 150] },
  'power_off': { priority: 'high', vibrate: [150, 50, 150] },
  'external_power_cut': { priority: 'high', vibrate: [150, 50, 150] },
  'power_on': { priority: 'low', vibrate: [50] },
  'power_restored': { priority: 'low', vibrate: [50] },
  
  // 📡 Connection
  'device_connected': { priority: 'low', vibrate: [30] },
  'device_disconnected': { priority: 'low', vibrate: [50, 30, 50] },
  'device_offline': { priority: 'medium', vibrate: [100, 50] },
  
  // ⏱️ Movement States
  'moving': { priority: 'low', vibrate: [30] },
  'stopped': { priority: 'low', vibrate: [30] },
  'idle': { priority: 'low', vibrate: [30] },
  'idle_too_long': { priority: 'medium', vibrate: [80] },
  'low_speed_alarm': { priority: 'medium', vibrate: [80] },
  
  // 🛢️ Fuel
  'fuel_theft': { priority: 'critical', vibrate: [200, 100, 200] },
  'fuel_drop': { priority: 'high', vibrate: [150, 50, 150] },
  
  // 📶 GPS
  'gps_signal_lost': { priority: 'medium', vibrate: [100, 50] },
  'gps_antenna_disconnected': { priority: 'medium', vibrate: [100, 50] },
  'gps_antenna_restored': { priority: 'low', vibrate: [50] },
  
  // 📳 Vibration
  'vibration_alarm': { priority: 'high', vibrate: [150, 50, 150] },
  
  // 🚫 Unauthorized
  'unauthorized_movement': { priority: 'critical', vibrate: [200, 100, 200] },
};

/**
 * Detect event type from various alarm formats
 * @param {Object} event - Event object with eventType, details, alarmStatus
 * @returns {string|null} - Normalized event type or null
 */
export const detectEventType = (event) => {
  if (!event) return null;
  
  const eventType = (event.eventType || '').toLowerCase().trim();
  const details = (event.details || '').toLowerCase().trim();
  const alarmStatus = (event.alarmStatus || '').toLowerCase().trim();
  
  // 🎯 Direct match first (fastest)
  if (ALARM_EVENTS[eventType]) return eventType;
  
  // 🚨 SOS/Emergency
  if (eventType.includes('sos') || alarmStatus.includes('sos') || details.includes('sos')) {
    return 'sos_alarm';
  }
  if (eventType.includes('panic')) return 'panic_button';
  
  // ⚡ Speed
  if (eventType.includes('overspeed') || eventType.includes('over_speed') || 
      eventType.includes('speeding') || details.includes('overspeed') || 
      details.includes('speed limit')) {
    return 'overspeed_alarm';
  }
  
  // 🔑 Ignition
  if (eventType.includes('ignition')) {
    return eventType.includes('on') || details.includes('on') ? 'ignition_on' : 'ignition_off';
  }
  
  // 🚗 ACC
  if (eventType.includes('acc')) {
    return eventType.includes('on') || details.includes('on') ? 'acc_on' : 'acc_off';
  }
  
  // 🚀 Engine
  if (eventType.includes('engine')) {
    return eventType.includes('start') || details.includes('start') ? 'engine_start' : 'engine_stop';
  }
  
  // 📍 Geofence
  if (eventType.includes('geofence') || eventType.includes('geo_fence') || 
      eventType.includes('area') || eventType.includes('fence')) {
    if (eventType.includes('enter') || details.includes('enter')) return 'geofence_entry';
    if (eventType.includes('exit') || details.includes('exit')) return 'geofence_exit';
    if (eventType.includes('violat')) return 'geofence_violation';
    return 'geofence_entry'; // default
  }
  
  // 🔋 Power
  if (eventType.includes('power_cut') || details.includes('power cut') || 
      eventType.includes('external_power')) {
    return 'power_cut';
  }
  if (eventType.includes('power_off')) return 'power_off';
  if (eventType.includes('power_on') || eventType.includes('power_restored') || 
      details.includes('power restored')) {
    return 'power_on';
  }
  
  // 🔋 Battery
  if (eventType.includes('low_battery') || eventType.includes('battery_low') || 
      eventType.includes('battery') && details.includes('low')) {
    return 'low_battery';
  }
  
  // 🚗 Harsh Driving
  if (eventType.includes('harsh_braking') || eventType.includes('harsh brak')) {
    return 'harsh_braking';
  }
  if (eventType.includes('harsh_acceleration') || eventType.includes('harsh accel')) {
    return 'harsh_acceleration';
  }
  if (eventType.includes('harsh_cornering') || eventType.includes('harsh corner')) {
    return 'harsh_cornering';
  }
  
  // 🚛 Towing/Tampering
  if (eventType.includes('tamper')) return 'tampering';
  if (eventType.includes('tow')) return 'towing_alarm';
  if (eventType.includes('vibration')) return 'vibration_alarm';
  
  // 📡 Connection
  if (eventType.includes('connected') && !eventType.includes('dis')) {
    return 'device_connected';
  }
  if (eventType.includes('disconnected') || eventType.includes('offline')) {
    return 'device_offline';
  }
  
  // 📶 GPS
  if (eventType.includes('gps') || eventType.includes('signal')) {
    if (eventType.includes('lost') || eventType.includes('disconnect')) {
      return 'gps_signal_lost';
    }
    if (eventType.includes('restored') || eventType.includes('connected')) {
      return 'gps_antenna_restored';
    }
  }
  
  // 🛢️ Fuel
  if (eventType.includes('fuel')) {
    if (eventType.includes('theft')) return 'fuel_theft';
    if (eventType.includes('drop')) return 'fuel_drop';
  }
  
  // 🚗 Movement Status
  if (eventType.includes('moving')) return 'moving';
  if (eventType.includes('stopped')) return 'stopped';
  if (eventType.includes('idle')) {
    return eventType.includes('long') || eventType.includes('too') ? 'idle_too_long' : 'idle';
  }
  
  // 💥 Collision
  if (eventType.includes('collision') || eventType.includes('crash')) {
    return 'collision_alarm';
  }
  if (eventType.includes('rollover') || eventType.includes('roll over')) {
    return 'rollover_alarm';
  }
  
  // 🚫 Unknown
  console.warn('⚠️ Unknown event type:', eventType, 'Details:', details);
  return null;
};

/**
 * Get alarm configuration for a given event type
 * @param {string} eventType - Event type key
 * @returns {Object} - Configuration with priority and vibrate pattern
 */
export const getAlarmConfig = (eventType) => {
  return ALARM_EVENTS[eventType] || { priority: 'low', vibrate: [30] };
};